import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import {
  Zap, LogIn, HelpCircle, Shield, Camera, Wifi, ArrowRight, Eye, Brain, Cog,
  Home, Mail, Users, Menu, DollarSign, Clock, BarChart2, Target, TrendingUp,
  UserCheck, Thermometer, Package, Building2, Car, Settings, RefreshCw, Globe,
  Sun, Moon, X, ChevronRight, Activity, ShieldCheck, Lock, Terminal, Flame, Monitor
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { BrandMark } from './BrandMark';

interface WelcomeScreenProps {
  onShowAuth: () => void;
  onShowSupport: () => void;
}

const NavButton: React.FC<{ icon: any; label: string; onClick: () => void; active?: boolean }> = ({ icon: Icon, label, onClick, active }) => (
  <Button
    variant="ghost"
    onClick={onClick}
    className={`text-blue-100/60 hover:text-white hover:bg-white/5 transition-all px-5 py-2.5 rounded-xl group text-[15px] font-semibold ${active ? 'bg-white/5 text-white' : ''}`}
  >
    <Icon className={`h-[18px] w-[18px] mr-2 transition-transform group-hover:scale-110 ${active ? 'text-blue-400' : ''}`} />
    {label}
  </Button>
);

const GlassCard: React.FC<{ children: React.ReactNode; className?: string; hover?: boolean; onClick?: () => void }> = ({ children, className = '', hover = true, onClick }) => (
  <div onClick={onClick} className={`bg-white/5 backdrop-blur-2xl border border-white/10 rounded-2xl p-6 transition-all duration-500 ${hover ? 'hover:bg-white/10 hover:border-white/20 hover:-translate-y-1 hover:shadow-[0_0_30px_rgba(59,130,246,0.1)]' : ''} ${className}`}>
    {children}
  </div>
);


const FeatureDetail: React.FC<{
  title: string;
  subtitle: string;
  description: string;
  icon: any;
  colorClass: string;
  bgColorClass: string;
  borderColorClass: string;
  capabilities: { icon: any; title: string; desc: string }[];
  metrics: { label: string; value: string }[];
  applications: { icon: any; title: string; desc: string }[];
  onBack: () => void;
}> = ({ title, subtitle, description, icon: Icon, colorClass, bgColorClass, borderColorClass, capabilities, metrics, applications, onBack }) => (
  <div className="min-h-screen bg-[#0a0b14] text-white p-6 relative overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-b from-blue-500/5 to-transparent pointer-events-none" />
    
    {/* Navigation */}
    <div className="max-w-7xl mx-auto flex justify-between items-center relative z-10 mb-12">
       <button onClick={onBack} className="p-3 bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 text-gray-400 hover:text-white transition-all">
         <X className="w-5 h-5" />
       </button>
    </div>

    <div className="max-w-5xl mx-auto relative z-10 pb-20">
      <div className="text-center mb-20">
        <div className={`w-24 h-24 rounded-full ${bgColorClass} ${borderColorClass} border flex items-center justify-center mx-auto mb-8 shadow-2xl`}>
          <Icon className={`w-12 h-12 ${colorClass}`} />
        </div>
        <h1 className="text-6xl font-bold tracking-tighter mb-4">{title}</h1>
        <p className={`text-xl font-medium ${colorClass} mb-6 tracking-wide uppercase`}>{subtitle}</p>
        <p className="text-blue-100/60 text-lg max-w-3xl mx-auto leading-relaxed font-light">{description}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
        {capabilities.map((cap, i) => (
          <GlassCard key={i} className="text-center group">
            <div className={`w-12 h-12 rounded-xl ${bgColorClass} flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform`}>
              <cap.icon className={`w-6 h-6 ${colorClass}`} />
            </div>
            <h3 className="text-xl font-bold mb-3">{cap.title}</h3>
            <p className="text-blue-100/40 text-sm leading-relaxed">{cap.desc}</p>
          </GlassCard>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20">
        <GlassCard hover={false} className="p-8">
          <h3 className={`text-2xl font-bold mb-8 flex items-center gap-3 ${colorClass}`}>
            <Activity className="w-6 h-6" /> Performance Metrics
          </h3>
          <div className="space-y-6">
            {metrics.map((m, i) => (
              <div key={i} className="flex justify-between items-center border-b border-white/5 pb-4 last:border-0">
                <span className="text-blue-100/60 font-light">{m.label}</span>
                <span className="text-white font-mono font-bold">{m.value}</span>
              </div>
            ))}
          </div>
        </GlassCard>
        
        <div className="grid grid-cols-1 gap-6">
          <h2 className="text-2xl font-bold text-center mb-2">Industry Applications</h2>
          <div className="grid grid-cols-2 gap-4">
            {applications.map((app, i) => (
              <div key={i} className="bg-white/2 backdrop-blur-xl border border-white/5 rounded-2xl p-6 hover:bg-white/5 transition-all text-center">
                <div className={`w-10 h-10 rounded-lg ${bgColorClass} flex items-center justify-center mx-auto mb-4`}>
                  <app.icon className={`w-5 h-5 ${colorClass}`} />
                </div>
                <h4 className="text-sm font-bold mb-2">{app.title}</h4>
                <p className="text-[10px] text-blue-100/40 leading-tight">{app.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

export function WelcomeScreen({ onShowAuth, onShowSupport }: WelcomeScreenProps) {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState<'home' | 'about' | 'contact' | 'pricing' | 'it-sees' | 'it-thinks' | 'it-adapts'>('home');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isEntering, setIsEntering] = useState(false);

  const features = [
    {
      id: 'it-sees',
      icon: Eye,
      title: "It sees.",
      description: "Advanced computer vision detects every movement, object, and anomaly in high-fidelity.",
      color: "text-blue-400",
      bgColor: "bg-blue-500/10",
      borderColor: "border-blue-500/20"
    },
    {
      id: 'it-thinks',
      icon: Brain,
      title: "It thinks.",
      description: "AI-powered analytics learn patterns and predict security threats before they manifest.",
      color: "text-purple-400",
      bgColor: "bg-purple-500/10",
      borderColor: "border-purple-500/20"
    },
    {
      id: 'it-adapts',
      icon: Cog,
      title: "It acts.",
      description: "Dynamic defense mechanisms that respond autonomously to environmental triggers.",
      color: "text-emerald-400",
      bgColor: "bg-emerald-500/10",
      borderColor: "border-emerald-500/20"
    }
  ];

  const handleNavigate = (page: any) => {
    setCurrentPage(page);
    setShowMobileMenu(false);
    window.scrollTo(0, 0);
  };

  const handleEnter = () => {
    setIsEntering(true);
    setTimeout(onShowAuth, 400);
  };

  if (currentPage === 'it-sees') {
    return (
      <FeatureDetail
        title="It Sees"
        subtitle="Advanced Computer Vision"
        description="Our AI-powered vision system detects, analyzes, and understands everything in your environment in real-time with unprecedented accuracy and intelligence."
        icon={Eye}
        colorClass="text-blue-400"
        bgColorClass="bg-blue-500/10"
        borderColorClass="border-blue-500/20"
        onBack={() => handleNavigate('home')}
        capabilities={[
          { icon: Users, title: "People Monitoring", desc: "Facial recognition and human behavior analysis for comprehensive safety" },
          { icon: Car, title: "Vehicle Intelligence", desc: "License plate recognition and classification with flow analysis" },
          { icon: Flame, title: "Safety Threats", desc: "Real-time detection of fire, smoke, and hazardous situations" },
          { icon: Package, title: "Object Tracking", desc: "Smart detection for inventory tracking and theft prevention" },
          { icon: Thermometer, title: "Environmental", desc: "Analysis of weather patterns and structural integrity" },
          { icon: Zap, title: "Edge Processing", desc: "Sub-second detection supporting high-density camera counts" }
        ]}
        metrics={[
          { label: "Accuracy Rate", value: "99.9%" },
          { label: "Processing Latency", value: "< 100ms" },
          { label: "Concurrent Objects", value: "500+" },
          { label: "False Positive Rate", value: "< 0.1%" },
          { label: "Detection Range", value: "Up to 500m" }
        ]}
        applications={[
          { icon: Shield, title: "Security", desc: "Perimeter and access control" },
          { icon: Camera, title: "Retail", desc: "Loss prevention & heatmaps" },
          { icon: Cog, title: "Industry", desc: "Process and safety monitoring" },
          { icon: Globe, title: "Infrastructure", desc: "Public safety and management" }
        ]}
      />
    );
  }

  if (currentPage === 'it-thinks') {
    return (
      <FeatureDetail
        title="It Thinks"
        subtitle="Cognitive Analytics Engine"
        icon={Brain}
        colorClass="text-purple-400"
        bgColorClass="bg-purple-500/10"
        borderColorClass="border-purple-500/20"
        description="Sophisticated neural networks that simulate cognitive processing to understand context, identify anomalies, and predict potential risks."
        onBack={() => handleNavigate('home')}
        capabilities={[
          { icon: Activity, title: "Anomaly Detection", desc: "Identification of deviations from normal operational patterns" },
          { icon: Brain, title: "Neural Learning", desc: "Continuous improvement of models based on site-specific data" },
          { icon: Target, title: "Object Intent", desc: "Predictive analysis of human and vehicle movement" },
          { icon: BarChart2, title: "Behavioral Insights", desc: "Deep statistical analysis of environmental interactions" },
          { icon: ShieldCheck, title: "Risk Scoring", desc: "Real-time evaluation of threat levels per node" },
          { icon: Zap, title: "Rapid Decisioning", desc: "Autonomous triage of alerts to reduce human fatigue" }
        ]}
        metrics={[
          { label: "Neural Layers", value: "1024+" },
          { label: "Model Confidence", value: "99.8%" },
          { label: "Training Samples", value: "50M+" },
          { label: "Update Frequency", value: "Real-time" },
          { label: "Scenario Simulations", value: "10k/sec" }
        ]}
        applications={[
          { icon: Terminal, title: "Command Center", desc: "Unified operational intelligence" },
          { icon: UserCheck, title: "Access Control", desc: "Identity-based decision gating" },
          { icon: TrendingUp, title: "Optimization", desc: "Resource allocation analytics" },
          { icon: Shield, title: "Defense", desc: "Advanced persistent threat mitigation" }
        ]}
      />
    );
  }

  if (currentPage === 'it-adapts') {
    return (
      <FeatureDetail
        title="It Acts"
        subtitle="Autonomous Response System"
        icon={Cog}
        colorClass="text-emerald-400"
        bgColorClass="bg-emerald-500/10"
        borderColorClass="border-emerald-500/20"
        description="Dynamic adaptation protocols that bridge the gap between perception and action, ensuring immediate mitigation of identified risks."
        onBack={() => handleNavigate('home')}
        capabilities={[
          { icon: Zap, title: "Instant Triggers", desc: "Immediate execution of safety and security protocols" },
          { icon: RefreshCw, title: "Self-Healing", desc: "Autonomous adjustment of camera parameters for clarity" },
          { icon: Lock, title: "Hardened Gating", desc: "Automatic locking of restricted sectors upon breach" },
          { icon: Mail, title: "Smart Routing", desc: "Dynamic alert delivery to the nearest responder" },
          { icon: Globe, title: "Edge Coordination", desc: "Device-to-device communication for swarm coverage" },
          { icon: Settings, title: "Deep Customization", desc: "User-defined response scripts for any event" }
        ]}
        metrics={[
          { label: "Response Time", value: "< 50ms" },
          { label: "Protocol Success", value: "99.99%" },
          { label: "Managed Nodes", value: "Unlimited" },
          { label: "Self-Optimization", value: "Continuous" },
          { label: "Uptime SLA", value: "99.999%" }
        ]}
        applications={[
          { icon: Building2, title: "Smart Facility", desc: "Integrated building management" },
          { icon: ShieldCheck, title: "Safe Perimeter", desc: "Autonomous sector containment" },
          { icon: Users, title: "Public Safety", desc: "Crowd control and evacuation" },
          { icon: Monitor, title: "Control Room", desc: "Enhanced situational awareness" }
        ]}
      />
    );
  }

  return (
    <div className={`min-h-screen bg-[#0a0b14] text-white flex flex-col transition-opacity duration-500 ${isEntering ? 'opacity-0' : 'opacity-100'}`}>
      {/* Premium Navbar */}
      <nav className="sticky top-0 z-50 bg-[#0a0b14]/80 backdrop-blur-2xl border-b border-white/5 px-8 py-5">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4 group cursor-pointer" onClick={() => handleNavigate('home')}>
            <BrandMark className="w-[96px] h-[52px] sm:w-[116px] sm:h-[64px] group-hover:scale-105 transition-transform duration-500 drop-shadow-[0_0_16px_rgba(59,130,246,0.45)]" />
            <div className="hidden sm:block">
              <p className="text-[2rem] md:text-[2.25rem] font-black tracking-[-0.02em] text-blue-50/95 leading-none">3RDI</p>
              <p className="text-[11px] md:text-xs tracking-[0.28em] uppercase text-blue-100/45 mt-1 font-semibold">Vision Intelligence Platform</p>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-2">
            <NavButton icon={Home} label="Home" onClick={() => handleNavigate('home')} active={currentPage === 'home'} />
            <NavButton icon={Users} label="About" onClick={() => navigate('/about')} />
            <NavButton icon={Mail} label="Contact" onClick={() => navigate('/contact')} />
            <NavButton icon={DollarSign} label="Pricing" onClick={() => navigate('/pricing')} />
            <div className="h-8 w-[1px] bg-white/10 mx-2" />
            <Button variant="ghost" onClick={() => navigate('/support')} className="text-blue-100/60 hover:text-white px-5 text-[15px] font-semibold">Support</Button>
            <button
              onClick={onShowAuth}
              className="bg-blue-600 hover:bg-blue-500 text-white px-7 py-3 rounded-xl font-bold text-[15px] transition-all shadow-[0_0_20px_rgba(37,99,235,0.3)] hover:scale-105 active:scale-95"
            >
              Sign In
            </button>
          </div>

          <button onClick={() => setShowMobileMenu(!showMobileMenu)} className="md:hidden p-2 text-gray-400">
            {showMobileMenu ? <X /> : <Menu />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {showMobileMenu && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-x-0 top-[88px] z-40 bg-[#0a0b14]/95 backdrop-blur-3xl border-b border-white/10 p-6 md:hidden flex flex-col gap-4"
          >
            {[
              { id: 'home', label: 'Home', icon: Home },
              { id: 'about', label: 'About', icon: Users, path: '/about' },
              { id: 'contact', label: 'Contact', icon: Mail, path: '/contact' },
              { id: 'pricing', label: 'Pricing', icon: DollarSign, path: '/pricing' },
              { id: 'support', label: 'Support', icon: HelpCircle, path: '/support' }
            ].map(item => (
              <button
                key={item.id}
                onClick={() => item.path ? navigate(item.path) : handleNavigate(item.id)}
                className="flex items-center gap-4 text-blue-100/60 p-4 rounded-xl hover:bg-white/5 hover:text-white transition-all"
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </button>
            ))}
            <button onClick={onShowAuth} className="w-full bg-blue-600 py-4 rounded-2xl font-bold mt-4">Login / Sign Up</button>
          </motion.div>
        )}
      </AnimatePresence>

      <main className="flex-1 relative overflow-hidden">
        {/* Background Atmosphere */}
        <div className="absolute inset-0 z-0">
          <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-blue-500/5 rounded-full blur-[120px] -mr-96 -mt-96" />
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-purple-500/5 rounded-full blur-[100px] -ml-64 -mb-64" />
        </div>

        <div className="max-w-7xl mx-auto px-6 pt-20 pb-32 relative z-10">
          {/* Hero Content */}
          <div className="text-center mb-32">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center gap-9"
            >
              <div className="relative group">
                <div className="absolute inset-0 bg-blue-500/20 blur-3xl group-hover:bg-blue-500/40 transition-all rounded-full" />
                <BrandMark className="w-[220px] h-[130px] sm:w-[280px] sm:h-[160px] relative z-10 drop-shadow-[0_0_26px_rgba(59,130,246,0.55)] group-hover:scale-105 transition-transform duration-700" />
              </div>
              
              <div className="space-y-5">
                <h1 className="text-[clamp(3rem,11vw,7rem)] font-black tracking-[-0.03em] text-white leading-none">
                  3RDI
                </h1>
                <p className="text-blue-100/45 tracking-[0.34em] uppercase text-[11px] sm:text-xs font-semibold">Vision Intelligence Platform</p>
                <div className="flex items-center justify-center gap-4 pt-2">
                  <div className="h-px w-12 bg-gradient-to-r from-transparent to-blue-500/50" />
                  <span className="text-[11px] text-blue-300/70 uppercase tracking-[0.22em] font-semibold">By BeAItle Defense</span>
                  <div className="h-px w-12 bg-gradient-to-l from-transparent to-blue-500/50" />
                </div>
              </div>

              <p className="text-lg sm:text-[1.35rem] text-blue-100/70 max-w-3xl font-normal leading-relaxed">
                AI-native surveillance with real-time detection, predictive analytics, and adaptive response built for enterprise-grade operations.
              </p>

              <button
                onClick={handleEnter}
                className="group relative px-12 py-5 rounded-full bg-blue-600 font-black text-xl overflow-hidden shadow-[0_0_40px_rgba(37,99,235,0.4)] transition-all hover:scale-105 active:scale-95"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-blue-600 to-violet-600 group-hover:scale-110 transition-transform" />
                <span className="relative z-10 flex items-center gap-3">
                  Deploy Node <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                </span>
              </button>
              
              <p className="text-[11px] text-blue-100/35 uppercase tracking-[0.22em] font-semibold mt-4">
                Advanced AI • Edge Optimized • Enterprise Grade
              </p>
            </motion.div>
          </div>

          {/* Interactive Core Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-32">
            {features.map((feature, i) => (
              <GlassCard key={i} className="cursor-pointer group" onClick={() => handleNavigate(feature.id)}>
                <div className={`w-14 h-14 rounded-2xl ${feature.bgColor} flex items-center justify-center mb-8 border ${feature.borderColor} group-hover:scale-110 transition-transform duration-500`}>
                  <feature.icon className={`w-7 h-7 ${feature.color}`} />
                </div>
                <h3 className="text-2xl font-bold mb-4">{feature.title}</h3>
                <p className="text-blue-100/40 text-sm leading-relaxed mb-6 italic">{feature.description}</p>
                <div className={`flex items-center gap-2 text-xs font-bold ${feature.color} opacity-0 group-hover:opacity-100 transition-all -translate-x-2 group-hover:translate-x-0`}>
                  EXPLORE TECHNOLOGY <ChevronRight className="w-4 h-4" />
                </div>
              </GlassCard>
            ))}
          </div>

          {/* Trust Section */}
          <GlassCard hover={false} className="mb-32 p-12 overflow-hidden relative">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 blur-[80px] -mr-32 -mt-32" />
            <h2 className="text-3xl font-bold mb-12 text-center tracking-tight">Trusted by Defense & Global Enterprise</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-12">
              {[
                { val: '99.9%', label: 'Detection Accuracy', color: 'text-emerald-400' },
                { val: '50ms', label: 'Response Latency', color: 'text-blue-400' },
                { val: '1M+', label: 'Hours Monitored', color: 'text-purple-400' },
                { val: '500+', label: 'Deployments', color: 'text-amber-400' }
              ].map((stat, i) => (
                <div key={i} className="text-center group">
                  <div className={`text-4xl font-black mb-2 font-mono ${stat.color} group-hover:scale-110 transition-transform`}>{stat.val}</div>
                  <div className="text-[10px] text-blue-100/40 uppercase tracking-widest font-bold">{stat.label}</div>
                </div>
              ))}
            </div>
          </GlassCard>

          {/* industry grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
               {[
                 { icon: Shield, label: "Government", desc: "Border security & public safety" },
                 { icon: Building2, label: "Corporate", desc: "Office & campus monitoring" },
                 { icon: Car, label: "Logistics", desc: "Fleet & warehouse tracking" },
                 { icon: Users, label: "Healthcare", desc: "Patient safety & fall detection" }
               ].map((item, i) => (
                 <div key={i} className="p-8 bg-white/2 border border-white/5 rounded-2xl hover:bg-white/5 transition-all group">
                   <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center mb-5 group-hover:bg-blue-500/20 transition-all">
                     <item.icon className="w-6 h-6 text-blue-400" />
                   </div>
                   <h4 className="font-bold text-lg mb-1">{item.label}</h4>
                   <p className="text-sm text-blue-100/40 font-light">{item.desc}</p>
                 </div>
               ))}
          </div>
        </div>
      </main>

      <footer className="py-20 border-t border-white/5 text-center mt-24">
        <p className="text-blue-100/25 text-sm font-medium tracking-[0.18em] uppercase">
          © 2026 3RDI VISION INTELLIGENCE <span className="mx-4">|</span> POWERED BY <span className="text-blue-500/50">BeAItle DEFENSE</span>
        </p>
      </footer>
    </div>
  );
}
